import type {
  CvCheckBand,
  CvCheckCategory,
  CvCheckInput,
  CvCheckRecommendation,
  CvCheckResult,
  CvCheckRuleId,
  CvCheckWarning,
} from './types';
import { CV_CHECK_RULES, cvCheckAllText } from './rules';
import { WARNING_PATTERNS } from './phrases';

const CATEGORIES: CvCheckCategory[] = ['essentials', 'impact', 'finnishFit', 'clarity', 'tone'];

export function bandForScore(score: number): CvCheckBand {
  if (score >= 80) return 'strong';
  if (score >= 55) return 'good';
  return 'needsWork';
}

/** Nothing to score yet: no headline, no roles, no education, no skills. */
function isCvEmpty(input: CvCheckInput): boolean {
  const { cv } = input;
  return (
    !cv.profileTitle.trim() &&
    !cv.profileSummary.trim() &&
    cv.profileWorkExperiences.length === 0 &&
    cv.profileEducation.length === 0 &&
    cv.profileSkills.filter((s) => s.trim()).length === 0
  );
}

export function findCvWarnings(input: CvCheckInput): CvCheckWarning[] {
  const text = cvCheckAllText(input);
  const warnings: CvCheckWarning[] = [];
  for (const { id, pattern } of WARNING_PATTERNS) {
    const m = text.match(pattern);
    if (m) warnings.push({ id, match: m[0] });
  }
  return warnings;
}

/**
 * Scores a CV against the rules in ./rules.ts. Dismissed rules ("not relevant for me") are
 * removed from both the earned and the maximum points, so a CV can still reach 100 honestly
 * when a rule genuinely doesn't fit the person.
 */
export function runCvCheck(input: CvCheckInput, dismissed: readonly string[] = []): CvCheckResult {
  const dismissedSet = new Set(dismissed);
  const categories = Object.fromEntries(CATEGORIES.map((c) => [c, { earned: 0, max: 0 }])) as CvCheckResult['categories'];
  const recommendations: CvCheckRecommendation[] = [];

  for (const rule of CV_CHECK_RULES) {
    if (dismissedSet.has(rule.id)) continue;
    const outcome = rule.evaluate(input);
    if (!outcome) continue;
    const earned = Math.max(0, Math.min(rule.maxPoints, outcome.earned));
    categories[rule.category].earned += earned;
    categories[rule.category].max += rule.maxPoints;
    const gain = Math.round(rule.maxPoints - earned);
    if (gain > 0) {
      recommendations.push({
        ruleId: rule.id,
        category: rule.category,
        gain,
        sectionId: rule.sectionId,
        values: outcome.values ?? {},
      });
    }
  }

  const earnedTotal = CATEGORIES.reduce((sum, c) => sum + categories[c].earned, 0);
  const maxTotal = CATEGORIES.reduce((sum, c) => sum + categories[c].max, 0);
  const score = maxTotal === 0 ? 0 : Math.round((earnedTotal / maxTotal) * 100);

  // Stable order: biggest gain first, then rule order so equal gains don't shuffle between renders.
  const order = new Map<CvCheckRuleId, number>(CV_CHECK_RULES.map((r, i) => [r.id, i]));
  recommendations.sort((a, b) => b.gain - a.gain || (order.get(a.ruleId) ?? 0) - (order.get(b.ruleId) ?? 0));

  return {
    score,
    band: bandForScore(score),
    isEmpty: isCvEmpty(input),
    recommendations,
    warnings: findCvWarnings(input),
    categories,
  };
}
